import _ from 'lodash';
import XLSX from 'xlsx';

export const LIST_TYPES: Readonly<{
    ALLOWED: 'ALLOWED';
}> = Object.freeze({
    ALLOWED: 'ALLOWED'
});

type valueof<T> = T[keyof T];

export type SingleListType = valueof<typeof LIST_TYPES>;

export enum IMPORT_EXPORT_PROCESS_TYPE {
    IMPORT = 'IMPORT',
    EXPORT = 'EXPORT'
}

export enum FILE_UPLOAD_STATUS {
    IDLE = 'IDLE',
    INITIATED = 'INITIATED',
    UPLOADING = 'UPLOADING',
    UPLOADED = 'UPLOADED'
}

export enum IMPORTER_START_UPLOAD_SCHEMA {
    FILE_NAME = 'fileName',
    FILE_DATA = 'file'
}

type ValidationObject = {
    method?: string;
    regex?: RegExp;
    message?: string;
    errMessage?: string;
};

type ImporterColumn = {
    acceptableHeaders?: string[];
    required?: boolean;
    isTokenField?: boolean;
    accessor?: string;
    valuesToCount?: string[];
    isAddColumn?: boolean;
    __count__?: {
        [key: string]: number;
        default: number;
    };
    validation?: {
        tests: ValidationObject[];
    };
};

const requiredHeadersAsStringArray = (acc: string[][], curr: ImporterColumn) => {
    if (!curr?.acceptableHeaders) return acc;
    if (Array.isArray(curr?.acceptableHeaders)) {
        return acc.concat([curr?.acceptableHeaders]);
    }
    return acc.concat([[curr?.acceptableHeaders]]);
};

const getHeaderRow = (sheet: any): string[] => {
    const headers = [];
    const range = XLSX.utils.decode_range(sheet['!ref']);
    const startingRowIndex = range.s.r; /* start in the first row */
    const startingColumnIndex = range.s.c;
    const endingColumnIndex = range.e.c;

    let C;
    /* walk every column in the range */
    for (C = startingColumnIndex; C <= endingColumnIndex; ++C) {
        const cell = sheet[XLSX.utils.encode_cell({ c: C, r: startingRowIndex })]; /* find the cell in the first row */

        let hdr = `UNKNOWN ${C}`; // <-- replace with your desired default
        if (cell?.t) hdr = XLSX.utils.format_cell(cell);

        headers.push(hdr);
    }
    return headers;
};

export const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    // eslint-disable-next-line
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

const headers = (workSheet: XLSX.WorkSheet, columns: ImporterColumn[]) => {
    // workSheet headers is [a, b, c, d]
    const workSheetHeaders = getHeaderRow(workSheet);
    // need the headers to match the acceptable columns that come from config (in order)
    const header = workSheetHeaders
        // we care about casing for headers
        .map((normalizedHeader) => {
            const columnConfig = columns.find((column) => {
                const result = column?.accessor === normalizedHeader;
                return result;
            });
            if (columnConfig?.accessor == null) {
                return null;
            }
            return columnConfig.accessor;
        });

    return header as string[];
};

/** Validate header row of csv file.
 * Returns difference array if columns are missing, or isEmptyFile as true if we have header but no data
 * @param workSheet single sheet of data to be validated
 * @param columns required columns to be validated against worksheet data
 */
export const validateCsvHeaders = (workSheet: XLSX.WorkSheet, columns: ImporterColumn[]) => {
    const requiredColumns = columns.reduce(requiredHeadersAsStringArray, []);
    const requiredDataColumns = columns?.filter((column) => column.required)?.map((column) => column?.accessor);

    // workSheet headers is [a, b, c, d]
    const workSheetHeaders = getHeaderRow(workSheet);

    const header = headers(workSheet, columns);
    const hasAtLeastOneHeader = !_.every(header, (v) => v === null);

    // assume user has given us tiny file so reading should be okay
    const workSheetAsJson = XLSX.utils
        // raw false converts all data to string
        .sheet_to_json(workSheet, { defval: '', header, raw: false })
        // strip out the header row
        ?.slice(1);
    const lowercaseColumnsArray = workSheetHeaders.map((workSheetHeader) => workSheetHeader);
    const difference = requiredColumns.filter((requiredHeader) => {
        // for the current required headers, there should be some value
        // inside of columnsArray that maps to it. If not, this "difference" in required headers and
        // the csv headers means a required header is missing.
        return !lowercaseColumnsArray.some((b) => requiredHeader.map((value) => value).includes(b as any));
    });

    if (!workSheetAsJson.length && hasAtLeastOneHeader) {
        return {
            difference: requiredColumns,
            isEmptyFile: true
        };
    }

    if (difference.length > 0) {
        return {
            difference
        };
    }

    const requiredColumnIndices = requiredDataColumns.map((requiredColumn) => {
        return lowercaseColumnsArray.indexOf(requiredColumn!);
    });

    const differenceObj: { [key: string]: string[] } = {};
    let missingCount = 0;
    workSheetAsJson?.forEach((row: any, rowIndex) => {
        requiredColumnIndices.forEach((columnIndex: number) => {
            const columnHeader = lowercaseColumnsArray[columnIndex];
            const cellValue = row[columnHeader];

            if (cellValue === undefined || cellValue === '') {
                if (!differenceObj[columnHeader]) {
                    differenceObj[columnHeader] = [];
                }
                missingCount++;
                differenceObj[columnHeader].push((rowIndex + 1).toString());
            }
        });
    });

    Object.keys(differenceObj).forEach((column) => {
        const rowsWithMissingData = differenceObj[column].join(', ');
        difference.push([`Column '${column}': Missing data in rows [${rowsWithMissingData}]`]);
    });

    return {
        difference,
        missingCount
    };
};
